import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuthMe } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import authAPI from "../../../services/authApi";
import StatusBadge from "../ui/StatusBadge";
import ClientPushSettings from "./ClientPushSettings";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import {
  Button,
  Card,
  Field,
  Input,
  PhoneInput,
  Select,
  FormSection,
  FormGrid,
  Skeleton,
  useToast,
} from "../../ui";

const GENDER_OPTIONS = [
  { value: "", label: "Не указан" },
  { value: "male", label: "Мужской" },
  { value: "female", label: "Женский" },
];

const EMPTY = {
  full_name: "",
  phone: "",
  date_of_birth: "",
  gender: "",
  address: "",
  consent_pd: false,
  consent_medical: false,
};

function patientToForm(p) {
  if (!p) return EMPTY;
  const card = p.patient_card || {};
  return {
    full_name: p.full_name || "",
    phone: p.phone || "",
    date_of_birth: p.date_of_birth || "",
    gender: card.gender || "",
    address: card.address || "",
    consent_pd: !!card.consent_pd,
    consent_medical: !!card.consent_medical,
  };
}

export default function PatientProfile() {
  const dispatch = useDispatch();
  const toast = useToast();
  const { profile } = useSelector((state) => state.auth);
  const [form, setForm] = useState(EMPTY);
  const [cardNumber, setCardNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    reminder_email_enabled: true,
  });
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      clinicAPI.getMyPatientProfile(),
      clinicAPI.getNotificationPrefs(),
    ])
      .then(([prof, n]) => {
        const p = prof.patient;
        setForm(patientToForm(p));
        setCardNumber(p?.patient_card?.card_number || "");
        setEmail(p?.email || "");
        setPrefs({
          reminder_email_enabled: n.reminder_email_enabled !== false,
        });
      })
      .catch((e) =>
        toast.error(e.response?.data?.error || "Не удалось загрузить профиль"),
      )
      .finally(() => setLoading(false));
  }, [toast]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await clinicAPI.updateMyPatientProfile({
        ...form,
        reminder_email_enabled: prefs.reminder_email_enabled,
      });
      await clinicAPI.updateNotificationPrefs(prefs);
      toast.success("Профиль сохранён");
      dispatch(fetchAuthMe());
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) {
      toast.error("Новый пароль от 8 символов");
      return;
    }
    setPwdSaving(true);
    try {
      await authAPI.changePassword(pwd.current, pwd.next);
      toast.success("Пароль изменён");
      setPwd({ current: "", next: "" });
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setPwdSaving(false);
    }
  };

  if (loading) {
    return (
      <Card title="Профиль">
        <Skeleton style={{ height: 24, width: "60%", marginBottom: 12 }} />
        <Skeleton style={{ height: 40, marginBottom: 8 }} />
        <Skeleton style={{ height: 40, marginBottom: 8 }} />
        <Skeleton style={{ height: 40 }} />
      </Card>
    );
  }

  const displayEmail =
    email && !email.includes("@patient.internal") ? email : null;

  return (
    <div className={s.portalPatientProfile}>
      <Card
        title="Профиль"
        subtitle={profile?.full_name || "Ваши данные"}
      >
        <form onSubmit={save}>
          <FormSection title="Контакты">
            <FormGrid>
              <Field label="ФИО">
                <Input
                  value={form.full_name}
                  onChange={(e) =>
                    setForm({ ...form, full_name: e.target.value })
                  }
                />
              </Field>
              <Field label="Телефон">
                <PhoneInput
                  value={form.phone || ""}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
              </Field>
              <Field label="Дата рождения">
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) =>
                    setForm({ ...form, date_of_birth: e.target.value })
                  }
                />
              </Field>
              {displayEmail ? (
                <Field label="Email" hint="Изменение — через регистратуру">
                  <Input value={displayEmail} disabled />
                </Field>
              ) : (
                <Field
                  label="Email"
                  hint="Не задан — обратитесь в клуб"
                >
                  <Input value="—" disabled />
                </Field>
              )}
            </FormGrid>
          </FormSection>

          <FormSection title="Дополнительно">
            <FormGrid>
              <Field label="Пол">
                <Select
                  value={form.gender}
                  onChange={(e) =>
                    setForm({ ...form, gender: e.target.value })
                  }
                >
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o.value || "none"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Адрес">
                <Input
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </Field>
            </FormGrid>
          </FormSection>

          <FormSection title="Согласия">
            <div className={s.portalConsentList}>
              <label className={s.consentCheck}>
                <input
                  type="checkbox"
                  checked={form.consent_pd}
                  onChange={(e) =>
                    setForm({ ...form, consent_pd: e.target.checked })
                  }
                />
                <span>
                  Согласие на обработку персональных данных (152-ФЗ)
                </span>
              </label>
              <label className={s.consentCheck}>
                <input
                  type="checkbox"
                  checked={form.consent_medical}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      consent_medical: e.target.checked,
                    })
                  }
                />
                <span>Согласие на участие в занятиях</span>
              </label>
            </div>
          </FormSection>

          <FormSection title="Push-уведомления">
            <ClientPushSettings />
          </FormSection>

          <FormSection title="Напоминания о занятиях">
            <div className={s.portalConsentList}>
              <label className={s.consentCheck}>
                <input
                  type="checkbox"
                  checked={prefs.reminder_email_enabled}
                  onChange={(e) =>
                    setPrefs({
                      ...prefs,
                      reminder_email_enabled: e.target.checked,
                    })
                  }
                />
                <span>Email за день до занятия</span>
              </label>
              <p className={u.hint}>
                Push о тренировках — в разделе «Push-уведомления» выше (приложение
                на главном экране). Email и push работают независимо.
              </p>
            </div>
          </FormSection>

          <div className={u.formActions}>
            <Button type="submit" disabled={saving}>
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Смена пароля" className={s.portalProfileCardSpaced}>
        <form onSubmit={changePassword}>
          <Field label="Текущий пароль">
            <Input
              type="password"
              value={pwd.current}
              onChange={(e) =>
                setPwd({ ...pwd, current: e.target.value })
              }
            />
          </Field>
          <Field label="Новый пароль">
            <Input
              type="password"
              minLength={8}
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            />
          </Field>
          <div className={u.formActions}>
            <Button type="submit" disabled={pwdSaving}>
              {pwdSaving ? "Смена…" : "Сменить пароль"}
            </Button>
            <Link to="/forgot-password" className={s.patientLink}>
              Забыли пароль?
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
